import { useContext, useEffect, useState } from 'react';
import AppContext, { AppData } from '../../AppContext';
import rolladen from '../../assets/img/rolladen-messen.jpg';
import Alert from '../common/Alert';
import Modal from '../common/Modal';
import Card from '../common/Card';
import './Rolladenmass.scss';
import ModalOpenIcon from '../common/ModalOpenIcon';
import { validateRolladen, ValidationMessage } from '../../utils/validations';
import { getBreite, getHoehe } from '../../utils/app-data';
import { appConfig } from '../../config';

function Rolladenmass() {
  const { appData, setDynamicAppData } = useContext(AppContext);

  const [breite, setBreite] = useState(getBreite(appData));
  const [hoehe, setHoehe] = useState(getHoehe(appData));
  const [validations, setValidations] = useState([] as ValidationMessage[]);

  const checkValidity = (data: AppData): boolean => {
    const checkedValidations = validateRolladen(data);
    setValidations(checkedValidations);
    return !checkedValidations.find((v) => v.severity === 'error');
  };

  const inputColor = (error: boolean, warning: boolean) => {
    if (error) {
      return 'error';
    }
    if (warning) {
      return 'warning';
    }
    return 'valid';
  };

  const isBreiteError = () => !!validations.find((v) => v.severity === 'error'
    && v.kEigenschaft.includes(appData.static.kEigenschaftBreite));
  const isBreiteWarning = () => !!validations.find((v) => v.severity === 'warning'
    && v.kEigenschaft.includes(appData.static.kEigenschaftBreite));
  const isHoeheError = () => !!validations.find((v) => v.severity === 'error'
    && v.kEigenschaft.includes(appData.static.kEigenschaftHoehe));
  const isHoeheWarning = () => !!validations.find((v) => v.severity === 'warning'
    && v.kEigenschaft.includes(appData.static.kEigenschaftHoehe));
  const isMaxFlaecheWarning = () => !!validations.find((v) => v.severity === 'warning'
    && v.kEigenschaft.includes(appData.static.kEigenschaftHoehe)
    && v.kEigenschaft.includes(appData.static.kEigenschaftBreite));

  const inputColorBreite = inputColor(isBreiteError(), isBreiteWarning() || isMaxFlaecheWarning());
  const inputColorHoehe = inputColor(isHoeheError(), isHoeheWarning() || isMaxFlaecheWarning());

  useEffect(() => {
    const newAppData = { ...appData };
    newAppData.dynamic.selectedVariationWerte[appData.static.kEigenschaftBreite] = breite;
    newAppData.dynamic.selectedVariationWerte[appData.static.kEigenschaftHoehe] = hoehe;
    const valid = checkValidity(newAppData);

    setDynamicAppData((dynamicAppData) => {
      const newDynamicAppData = { ...dynamicAppData };
      newDynamicAppData.invalid = !valid;
      newDynamicAppData.selectedVariationWerte[appData.static.kEigenschaftBreite] = breite;
      newDynamicAppData.selectedVariationWerte[appData.static.kEigenschaftHoehe] = hoehe;
      return newDynamicAppData;
    });
  }, [breite, hoehe]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="configurator__rolladenmass">
      <Card>
        <div className="d-flex flex-row justify-content-between align-items-center">
          <h3>Rolladenmaß</h3>
          <ModalOpenIcon modalId="infoModalRolladenmass" />
        </div>
        <Modal
          id="infoModalRolladenmass"
          title="Rolladenmaß"
        >
          <div className="row">
            <div className="col-6">
              <h3>Richtige Breite ermitteln</h3>
              <p>
                Die Breite des Behangs wird an <strong>drei Punkten in den Führungsschienen (C)</strong> gemessen.
                Nehmen Sie dazu jeweils das Maß im oberen <strong>1</strong>, mittleren <strong>(2)</strong> und
                unteren <strong>(3)</strong> Bereich der Führungsschienen.
              </p>
              <p>
                Von diesen drei Maßen wählen Sie dann das <strong>kleinste</strong> und ziehen davon 10 mm (1 cm) ab.
                Daraus ergibt sich dann das fertige Rolladenmaß <strong>(lichte Breite)</strong> inklusive Luft.
              </p>
              <Alert severity="warning">
                Hinweis:
                <br />
                Bitte achten Sie darauf, dass das fertige Rolladenmaß nicht kleiner als das Maß zwischen den Führungsschienen ist.
              </Alert>
            </div>
            <div className="col-6">
              <h3>Richtige Höhe ermitteln</h3>
              <p>
                Die Höhe des Rolladenpanzers ergibt sich aus dem lichten Maß zwischen der unteren Kante des
                Rolladenkastens <strong>(A)</strong> und der oberen Kante der
                Fensterbank <strong>(D)</strong> <strong>(lichte Höhe)</strong>.
              </p>
              <p>
                Wenn Sie das lichte Maß nun ermittelt haben, addieren Sie nochmals 150 mm (15 cm) zum ermittelten Maß hinzu.
              </p>
              <Alert severity="warning">
                Hinweis:
                <br />
                Bitte beachten Sie, dass Sie das Maß so nah wie möglich am Fenster nehmen, da Fensterbänke in der Regel
                mit einer gewissen Neigung montiert sind und dadurch das Maß verfälscht werden kann.
              </Alert>
            </div>
          </div>
        </Modal>
        <hr />
        <div className="mt-3 d-flex flex-row flex-wrap gap-2">
          <div className="image">
            <img src={appConfig.basePath + rolladen} alt="" style={{ width: 'inherit' }} />
          </div>
          <div className="desc">
            <p>An Punkt 1,2 und 3 in der Führungsschiene messen. Das kleinste Maß -1cm geben Sie als Breite an!</p>
            <p className="mt-3 attention">Bei jedem Rolladenpanzer sind immer 2 Anschlagstopper mit 40mm Länge enthalten. (ausgenommen Anschlagprofile)</p>
            <p className="mt-3 attention">Außerdem enthalten sind immer die passenden Aufhängefedern.</p>
            <div className="inputs mt-3">
              <div className="input-group mb-2">
                <div className="input-group-prepend">
                  <span className="input-group-text">Breite</span>
                </div>
                <input
                  className={`${inputColorBreite} form-control`}
                  type="text"
                  value={breite}
                  placeholder="Breite"
                  pattern="^[0-9]*$"
                  onChange={(e) => {
                    if (!Number.isNaN(Number(e.target.value))) {
                      setBreite(Number(e.target.value));
                    }
                  }}
                />
                <div className="input-group-append">
                  <span className="input-group-text">mm</span>
                </div>
              </div>
              <div className="input-group">
                <div className="input-group-prepend">
                  <span className="input-group-text">Höhe</span>
                </div>
                <input
                  className={`${inputColorHoehe} form-control`}
                  type="text"
                  value={hoehe}
                  placeholder="Höhe"
                  pattern="^[0-9]*$"
                  onChange={(e) => {
                    if (!Number.isNaN(Number(e.target.value))) {
                      setHoehe(Number(e.target.value));
                    }
                  }}
                />
                <div className="input-group-append">
                  <span className="input-group-text">mm</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          {validations.map((v) => v.messageContent)}
        </div>
      </Card>
    </div>
  );
}

export default Rolladenmass;
